import { rewriteEntityTerms } from '@/lib/terminology/entity-terms'

import {
  readApiErrorMessage as extractMessageFromData,
  readApiErrorStatus as extractStatusFromData,
  isLostResponseError as isTransportLostResponseError,
  parseApiErrorText,
} from '@scrambl/shared/api-errors'

import { InvalidJsonResponseError } from './json-response'

export { parseApiErrorText } from '@scrambl/shared/api-errors'

type ErrorData = Record<string, unknown>

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback

  if (typeof error === 'string') {
    return parseApiErrorText(error, fallback).message
  }

  const errorObj = error as {
    message?: unknown
    data?: unknown
    response?: { data?: unknown }
  }

  const dataMessage = extractMessageFromData(
    errorObj.data ?? errorObj.response?.data,
  )
  if (dataMessage) return dataMessage

  if (typeof errorObj.message === 'string') {
    return parseApiErrorText(errorObj.message, fallback).message
  }

  const objectMessage = extractMessageFromData(errorObj)
  return objectMessage ?? fallback
}

// API messages say "entity"; users see the org's configured term
export const getErrorMessage = (
  error: unknown,
  fallback = 'An error occurred',
): string => rewriteEntityTerms(resolveErrorMessage(error, fallback))

export const getErrorStatus = (error: unknown): number | undefined => {
  if (!error) return undefined

  if (typeof error === 'object') {
    const errorObj = error as {
      status?: unknown
      statusCode?: unknown
      response?: { status?: unknown; data?: unknown }
      data?: unknown
    }

    const directStatus =
      errorObj.status ?? errorObj.statusCode ?? errorObj.response?.status
    const validatedStatus = extractStatusFromData({ status: directStatus })
    if (validatedStatus !== undefined) return validatedStatus

    const dataStatus = extractStatusFromData(
      errorObj.data ?? errorObj.response?.data,
    )
    if (typeof dataStatus === 'number') {
      return dataStatus
    }
  }

  const message =
    typeof error === 'string' ? error : (error as Error | undefined)?.message
  if (typeof message !== 'string') return undefined

  const match = message.match(/API Error\s+(\d+)/i)
  if (match) {
    const status = Number(match[1])
    return extractStatusFromData({ status })
  }

  return undefined
}

export const isAuthenticationAvailabilityError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const errorObj = error as {
    data?: unknown
    response?: { data?: unknown }
  }
  const data = errorObj.data ?? errorObj.response?.data
  if (!data || typeof data !== 'object') return false

  const payload = data as ErrorData
  return (
    payload.code === 'AUTH_PROVIDER_UNAVAILABLE' ||
    payload.reason === 'transient'
  )
}

export const isAICreditsExhaustedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const errorObj = error as {
    code?: unknown
    data?: unknown
    response?: { data?: unknown }
  }
  const data = errorObj.data ?? errorObj.response?.data ?? errorObj
  return (
    !!data &&
    typeof data === 'object' &&
    (data as ErrorData).code === 'AI_CREDITS_EXHAUSTED'
  )
}

export function isLostResponseError(error: unknown): boolean {
  // A proxy/deadline failure does not establish whether the upstream write committed.
  const status = getErrorStatus(error)
  return (
    status === 408 ||
    status === 502 ||
    status === 504 ||
    error instanceof InvalidJsonResponseError ||
    isTransportLostResponseError(error)
  )
}

/** The command committed, but the API could not return a permitted current view. */
export function isCommittedTaskWriteError(error: unknown): boolean {
  if (getErrorStatus(error) !== 409 || !error || typeof error !== 'object')
    return false
  const source = error as { data?: unknown; response?: { data?: unknown } }
  const data = source.data ?? source.response?.data
  if (!data || typeof data !== 'object') return false
  const receipt = data as Record<string, unknown>
  return (
    receipt.code === 'TASK_WRITE_COMMITTED' &&
    receipt.writeCommitted === true &&
    typeof receipt.taskId === 'string'
  )
}
